import React, { createContext, useContext, useEffect, useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";

const SiteContentContext = createContext(null);

/**
 * localStorage cache — gives instant render with the last known logo/title/hero
 * on page refresh so the browser doesn't flash the default fallback while the
 * network request to /api/site-content is in flight (stale-while-revalidate).
 */
const CACHE_KEY = "ms_site_content_v1";

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch { /* quota / privacy mode */ }
}

function applyBrandToDocument(brand) {
    if (!brand) return;
    // Title
    if (brand.name) {
        document.title = brand.name;
    }
    // Favicon
    const faviconUrl = brand.favicon_url ? resolveImageUrl(brand.favicon_url) : null;
    if (faviconUrl) {
        // Remove existing icon links to avoid duplicates / stale icons
        document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());
        const link = document.createElement("link");
        link.rel = "icon";
        link.type = "image/png";
        link.href = faviconUrl;
        document.head.appendChild(link);

        const apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        apple.href = faviconUrl;
        document.head.appendChild(apple);
    }
}

/**
 * Apply CMS theme (colors, fonts, radius) as CSS variables on :root.
 * Tailwind utilities continue to work (sky-500 etc.) — these vars are an
 * additive layer used by the BlockRenderer + a few global accents.
 */
function hexToRgb(hex) {
    if (!hex || typeof hex !== "string") return null;
    const m = hex.trim().replace("#", "");
    if (m.length !== 6 && m.length !== 3) return null;
    const f = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
    const r = parseInt(f.slice(0, 2), 16);
    const g = parseInt(f.slice(2, 4), 16);
    const b = parseInt(f.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return `${r} ${g} ${b}`;
}

const GOOGLE_FONT_STACK = new Set([
    "Inter", "Poppins", "Roboto", "Montserrat", "Open Sans", "Lato",
    "Nunito", "Raleway", "Playfair Display", "Manrope", "Outfit",
    "Plus Jakarta Sans", "DM Sans", "Quicksand", "Work Sans",
]);

function ensureFontLoaded(fam) {
    if (!fam || !GOOGLE_FONT_STACK.has(fam)) return;
    const id = `gfont-${fam.replace(/\s+/g, "-").toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fam.replace(/\s+/g, "+")}:wght@400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
}

function applyThemeToDocument(theme) {
    if (!theme) return;
    const root = document.documentElement;
    const set = (k, v) => v && root.style.setProperty(k, v);
    set("--ms-primary", theme.primary);
    set("--ms-primary-hover", theme.primary_hover);
    set("--ms-accent", theme.accent);
    set("--ms-success", theme.success);
    set("--ms-danger", theme.danger);
    // RGB triplets for opacity utility (e.g. rgb(var(--ms-primary-rgb) / 0.1))
    const rgb = hexToRgb(theme.primary);
    if (rgb) set("--ms-primary-rgb", rgb);
    set("--ms-radius", theme.radius);
    const shadowMap = {
        sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    };
    set("--ms-shadow", shadowMap[theme.shadow_intensity] || shadowMap.md);
    if (theme.font_family) {
        ensureFontLoaded(theme.font_family);
        set("--ms-font-body", `'${theme.font_family}', system-ui, sans-serif`);
    }
    if (theme.font_display) {
        ensureFontLoaded(theme.font_display);
        set("--ms-font-display", `'${theme.font_display}', system-ui, sans-serif`);
    }
    if (theme.dark_mode_default && !root.classList.contains("dark") && localStorage.getItem("theme_initialized") !== "1") {
        root.classList.add("dark");
        localStorage.setItem("theme_initialized", "1");
    }
}

export function SiteContentProvider({ children }) {
    // Start with the cached payload so first paint is correct (no flash).
    const [content, setContent] = useState(() => {
        const cached = readCache();
        if (cached) {
            // Apply cached branding + theme immediately on mount before the network call.
            applyBrandToDocument(cached.brand);
            applyThemeToDocument(cached.theme);
        }
        return cached;
    });

    useEffect(() => {
        api.get("/site-content").then((r) => {
            const data = r.data || {};
            setContent(data);
            applyBrandToDocument(data.brand);
            applyThemeToDocument(data.theme);
            writeCache(data);
        }).catch(() => {
            // Keep showing cached content; only fall back to empty if there was nothing cached.
            setContent((c) => c || {});
        });
    }, []);

    return <SiteContentContext.Provider value={content}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
    return useContext(SiteContentContext) || {};
}

export function useSiteContentLoaded() {
    return useContext(SiteContentContext) !== null;
}
